import * as yup from 'yup';
import i18n from 'helpers/i18n';
import messages from './messages';
import { clsx } from 'clsx';
import { useId } from 'react';
import { Domain } from 'types/api/envScales/Domain';
import { Indicator } from 'types/api/envScales/Indicator';
import { useServerFieldError } from 'components/Measure/View/ValidationsContext';
import { RadioGroup, RadioGroupItem } from '@radix-ui/react-radio-group';
import { AlertCircleFilled } from '@teachstonellc/component-library/dist/icons';

interface RangeRadioGroupItemProps {
  value: string;
  children: React.ReactNode;
}

function RangeRadioGroupItem({ value, children }: RangeRadioGroupItemProps) {
  const classes = clsx(
    'px-4 py-1 text-center rounded-xl capitalize transition-colors',
    // base state
    'bg-ds-white hover:bg-ds-surface-hover',
    // checked state
    'data-[state=checked]:bg-scale-green data-[state=checked]:text-white'
  );

  return (
    <RadioGroupItem value={value} className={classes}>
      {children}
    </RadioGroupItem>
  );
}

type IndicatorScoreValues = -1 | 0 | 50 | 100 | null;

export interface IndicatorScoreProps {
  /** Indicator. */
  indicator: Indicator;

  /** Indicator score value (-1, 0, 50, 100). */
  value: IndicatorScoreValues | undefined;

  /** Domain. */
  domain: Domain;

  /** Callback fired when the indicator score changes. */
  onChange: (indicatorId: number, value: IndicatorScoreValues) => void;

  /** Observation Cycle Number. */
  cycleNumber: number;

  /** Dimension t_name. */
  dimension: string;

  /** Indicates if the observation is informal. */
  isInformal: boolean;
}

export function IndicatorScoreInput({
  indicator,
  value,
  domain,
  onChange,
  cycleNumber,
  dimension,
  isInformal
}: IndicatorScoreProps) {
  const fieldId = useId();
  const indicatorError = useServerFieldError(
    `cycles.${cycleNumber}.scores.${dimension}.indicators.${indicator.t_name}`,
    value,
    isInformal ? yup.number().nullable() : yup.number().required()
  );

  function onValueChange(value: string) {
    onChange(indicator.id, parseInt(value) as IndicatorScoreValues);
  }

  function skipMid() {
    return (
      domain.class_version === 2 && indicator.t_name === 'severe_negativity'
    );
  }

  return (
    <>
      <div className="mt-3 md:mt-0">
        <span id={fieldId} className="font-bold">
          {indicator.name}
          {indicatorError && (
            <>
              <AlertCircleFilled
                size={20}
                className="text-ds-icon-negative ml-[5px] inline align-text-bottom"
              />
              <span className="text-scale-red text-sm font-normal block mt-1 md:inline md:mt-0 md:ml-1">
                {i18n.ft(messages.errors.required)}
              </span>
            </>
          )}
        </span>
      </div>

      <RadioGroup
        aria-labelledby={fieldId}
        onValueChange={onValueChange}
        className="grid grid-cols-4 gap-3 w-fit"
        value={value !== null && value !== undefined ? value.toString() : ''}
      >
        <RangeRadioGroupItem value="0">
          {i18n.ft(messages.low)}
        </RangeRadioGroupItem>

        {skipMid() ? (
          <div className="col-span-1 w-full" />
        ) : (
          <RangeRadioGroupItem value="50">
            {i18n.ft(messages.mid)}
          </RangeRadioGroupItem>
        )}

        <RangeRadioGroupItem value="100">
          {i18n.ft(messages.high)}
        </RangeRadioGroupItem>

        {!indicator.required && (
          <div className="col-span-3 md:col-auto">
            <RangeRadioGroupItem value="-1">
              <span className="md:hidden">
                {i18n.ft(messages.notApplicable)}
              </span>
              <span className="hidden md:inline">
                {i18n.ft(messages.notApplicableDesktop)}
              </span>
            </RangeRadioGroupItem>
          </div>
        )}
      </RadioGroup>
    </>
  );
}
